// =====================================================
// useInactivityLogout
// Detecta inactividad del usuario y ejecuta un callback
// (logout) tras el tiempo configurado.
//
// Actividad detectada: mousemove, mousedown, keydown,
// touchstart, scroll, wheel, visibilitychange (al volver
// a la pestaña el timer se evalúa correctamente).
//
// El hook se desactiva completamente cuando:
//   · enabled = false
//   · el usuario no está autenticado (isAuthenticated = false)
//
// Así garantizamos cero side-effects en desarrollo o
// cuando la feature está desactivada.
// =====================================================
import { useEffect, useRef, useCallback } from 'react';

// Eventos que consideramos actividad real del usuario
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'wheel',
];

interface UseInactivityLogoutOptions {
  /** Minutos de inactividad antes del logout. Default: 15 */
  timeoutMinutes?: number;
  /** Si false el hook no hace nada. Default: false */
  enabled?: boolean;
  /** Si false (no autenticado) el hook no hace nada */
  isAuthenticated: boolean;
  /** Función que se llama al detectar inactividad */
  onTimeout: () => void;
}

export function useInactivityLogout({
  timeoutMinutes = 15,
  enabled = false,
  isAuthenticated,
  onTimeout,
}: UseInactivityLogoutOptions): void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref estable al callback para no recrear listeners en cada render
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const resetTimer = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      onTimeoutRef.current();
    }, timeoutMinutes * 60 * 1000);
  }, [clearTimer, timeoutMinutes]);

  useEffect(() => {
    // No hacer nada si está desactivado o el usuario no está autenticado
    if (!enabled || !isAuthenticated) {
      clearTimer();
      return;
    }

    // Arrancar el timer desde el momento en que se activa
    resetTimer();

    // Registrar listeners en window
    const handleActivity = () => resetTimer();

    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Al volver a la pestaña: si el timer ya expiró mientras la pestaña
    // estaba oculta, el setTimeout lo habrá disparado igualmente.
    // No necesitamos lógica extra — el timer sigue corriendo en background.

    return () => {
      clearTimer();
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [enabled, isAuthenticated, resetTimer, clearTimer]);
}
